// The identify() dedupe decision, extracted so it can actually be tested.
//
// identify() emits a $set every time it is called, and it was firing about once
// per page load (measured: a median of 1-2 $set per person per day). Suppressing
// the repeat is worth ~20-25k events/mo, but the comparison below is the single
// branch that decides whether analytics sees a user at all, so it lives here as
// a pure function rather than inline in App.vue where nothing could reach it.
// See ADR-0036.

// Stable string identity for a [userId, personProps] pair. Key order comes from
// buildPersonProps, which is deterministic, so the same identity always yields
// the same string. account_age_days is deliberately part of it: that is what
// makes a returning user re-identify once a day, so person properties never go
// stale by more than that.
export function identityFingerprint(
	userId: string,
	personProps: Record<string, unknown>,
): string {
	return JSON.stringify([userId, personProps]);
}

// Should identify() be called for this fingerprint?
//
// `lastInMemory` is the same-session guard (null on a fresh page load);
// `lastPersisted` carries the dedupe ACROSS loads, which is where the duplicate
// $set events actually come from. Either matching means we have already told
// PostHog this exact identity.
//
// Both null (first ever load, or storage unavailable) => identify, which is the
// pre-dedupe behaviour and the correct thing to degrade to.
export function shouldIdentify(
	fingerprint: string,
	lastInMemory: string | null,
	lastPersisted: string | null,
): boolean {
	// `||` not `??`: an empty string is not a meaningful fingerprint either, so
	// treating it as "nothing recorded" is right.
	const last: string | null = lastInMemory || lastPersisted;
	return fingerprint !== last;
}
